<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { ChevronDown, ChevronUp, Check, Search, Info, X, Minus } from 'lucide-vue-next'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Button } from '@/components/ui/button'
  import { Checkbox } from '@/components/ui/checkbox'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { type GenericDataMapper, LawFieldEnum } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { ChevronsUpDown } from 'lucide-vue-next'
  import { lawFieldsDE, lawFieldsEN, lawFieldsFR } from '@/i18n/lawFields'

  const BETEILIGTEDATEN_KEY = 'Beteiligtedaten'

  const { t, locale } = useTranslation({
    en: {
      notSelected: 'Not selected',
      searchFields: 'Search for fields',
      searchCategories: 'Search',
      fieldType: 'Field type',
      selectAll: 'Select all',
      participantDataInfo:
        'If you want to select participant data, change the section type from "law specific" to "participant"',
      participantDataOnly: 'Beteiligtedaten can only be selected in participant sections',
      noResults: 'No results found',
      noFieldTypeSelected:
        'No field type selected. Please select at least one field type from the filter.',
      beteiligtedaten: 'Participant data'
    },
    de: {
      notSelected: 'Nicht ausgewählt',
      searchFields: 'Felder suchen',
      searchCategories: 'Suchen',
      fieldType: 'Feldtyp',
      selectAll: 'Alle auswählen',
      participantDataInfo:
        'Wenn Sie Beteiligtedaten auswählen möchten, ändern Sie den Abschnittstyp von "rechtsspezifisch" auf "Beteiligter"',
      participantDataOnly:
        'Beteiligtedaten können nur in Beteiligten-Abschnitten ausgewählt werden',
      noResults: 'Keine Ergebnisse gefunden',
      noFieldTypeSelected:
        'Kein Feldtyp ausgewählt. Bitte wählen Sie mindestens einen Feldtyp aus dem Filter.',
      beteiligtedaten: 'Beteiligtedaten'
    }
  })

  interface Props {
    modelValue: string | null
    tags: GenericDataMapper[]
    sectionType: 'law_specific' | 'participant'
    questionnaireLawField?: string | null
    disabled?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    questionnaireLawField: null
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string | null): void
  }>()

  // Popover states
  const isMainPopoverOpen = ref(false)
  const isFilterPopoverOpen = ref(false)

  // Search states
  const fieldSearchQuery = ref('')
  const filterSearchQuery = ref('')

  // Expanded categories state
  const expandedCategories = ref<Set<string>>(new Set())

  // Selected filter categories
  const selectedFilterCategories = ref<Set<string>>(new Set())

  // Get all unique categories from tags
  const allCategories = computed(() => {
    const categories = new Set<string>()

    // Add Beteiligtedaten as a special category
    const hasEntityFields = props.tags.some((tag) => tag.is_entity_field)
    if (hasEntityFields) {
      categories.add(BETEILIGTEDATEN_KEY)
    }

    // Add law fields
    props.tags.forEach((tag) => {
      if (!tag.is_entity_field && tag.law_field) {
        categories.add(tag.law_field)
      } else if (!tag.is_entity_field && !tag.law_field) {
        // Tags without law_field go to Allgemein
        categories.add(LawFieldEnum.ALLGEMEIN)
      }
    })

    return Array.from(categories).sort((a, b) => {
      // Beteiligtedaten always first
      if (a === BETEILIGTEDATEN_KEY) return -1
      if (b === BETEILIGTEDATEN_KEY) return 1
      // Allgemein second
      if (a === LawFieldEnum.ALLGEMEIN) return -1
      if (b === LawFieldEnum.ALLGEMEIN) return 1
      return a.localeCompare(b)
    })
  })

  // Group tags by category
  const groupedTags = computed(() => {
    const groups: Record<string, GenericDataMapper[]> = {}

    props.tags.forEach((tag) => {
      let category: string
      if (tag.is_entity_field) {
        category = BETEILIGTEDATEN_KEY
      } else if (tag.law_field) {
        category = tag.law_field
      } else {
        category = LawFieldEnum.ALLGEMEIN
      }

      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(tag)
    })

    // Sort tags within each group
    Object.keys(groups).forEach((key) => {
      groups[key].sort((a, b) => a.label.localeCompare(b.label))
    })

    return groups
  })

  // Initialize selected filter categories based on section type and law field
  const initializeFilters = () => {
    selectedFilterCategories.value.clear()

    if (props.sectionType === 'participant') {
      // For participant sections, pre-select Beteiligtedaten
      selectedFilterCategories.value.add(BETEILIGTEDATEN_KEY)
    } else {
      // For law-specific sections, pre-select Allgemein and the questionnaire's law field
      selectedFilterCategories.value.add(LawFieldEnum.ALLGEMEIN)
      if (props.questionnaireLawField && props.questionnaireLawField !== LawFieldEnum.ALLGEMEIN) {
        selectedFilterCategories.value.add(props.questionnaireLawField)
      }
    }
  }

  // Watch for changes in section type or law field to reinitialize filters
  watch(
    () => [props.sectionType, props.questionnaireLawField],
    () => {
      initializeFilters()
    },
    { immediate: true }
  )

  // Filter categories for the filter dropdown
  const filteredFilterCategories = computed(() => {
    if (!filterSearchQuery.value.trim()) {
      return allCategories.value
    }
    const query = filterSearchQuery.value.toLowerCase()
    return allCategories.value.filter((cat) => cat.toLowerCase().includes(query))
  })

  // Check if Beteiligtedaten is disabled (only enabled in participant sections)
  const isBeteiligtedatenDisabled = computed(() => {
    return props.sectionType !== 'participant'
  })

  // Get count for a category
  const getCategoryCount = (category: string) => {
    return groupedTags.value[category]?.length || 0
  }

  // Toggle filter category selection
  const toggleFilterCategory = (category: string) => {
    if (category === BETEILIGTEDATEN_KEY && isBeteiligtedatenDisabled.value) {
      return
    }

    if (selectedFilterCategories.value.has(category)) {
      selectedFilterCategories.value.delete(category)
    } else {
      selectedFilterCategories.value.add(category)
    }
    // Force reactivity update
    selectedFilterCategories.value = new Set(selectedFilterCategories.value)
  }

  // Select/deselect all filter categories
  const toggleSelectAll = () => {
    const selectableCategories = allCategories.value.filter(
      (cat) => !(cat === BETEILIGTEDATEN_KEY && isBeteiligtedatenDisabled.value)
    )

    const allSelected = selectableCategories.every((cat) => selectedFilterCategories.value.has(cat))

    if (allSelected) {
      // Deselect all
      selectedFilterCategories.value.clear()
    } else {
      // Select all selectable categories
      selectableCategories.forEach((cat) => selectedFilterCategories.value.add(cat))
    }
    // Force reactivity update
    selectedFilterCategories.value = new Set(selectedFilterCategories.value)
  }

  // Check if all categories are selected
  const isAllSelected = computed(() => {
    const selectableCategories = allCategories.value.filter(
      (cat) => !(cat === BETEILIGTEDATEN_KEY && isBeteiligtedatenDisabled.value)
    )
    return (
      selectableCategories.length > 0 &&
      selectableCategories.every((cat) => selectedFilterCategories.value.has(cat))
    )
  })

  // Check if some (but not all) categories are selected
  const isSomeSelected = computed(() => {
    const selectableCategories = allCategories.value.filter(
      (cat) => !(cat === BETEILIGTEDATEN_KEY && isBeteiligtedatenDisabled.value)
    )
    const selectedCount = selectableCategories.filter((cat) =>
      selectedFilterCategories.value.has(cat)
    ).length
    return selectedCount > 0 && selectedCount < selectableCategories.length
  })

  // Filtered and grouped tags based on search and filter
  const filteredGroupedTags = computed(() => {
    const result: Record<string, GenericDataMapper[]> = {}
    const searchQuery = fieldSearchQuery.value.toLowerCase().trim()

    allCategories.value.forEach((category) => {
      // Skip categories that aren't selected in the filter
      if (
        selectedFilterCategories.value.size > 0 &&
        !selectedFilterCategories.value.has(category)
      ) {
        return
      }

      const tags = groupedTags.value[category] || []
      const filteredTags = searchQuery
        ? tags.filter((tag) => tag.label.toLowerCase().includes(searchQuery))
        : tags

      if (filteredTags.length > 0) {
        result[category] = filteredTags
      }
    })

    return result
  })

  // Get sorted categories for display
  const sortedCategories = computed(() => {
    return Object.keys(filteredGroupedTags.value).sort((a, b) => {
      if (a === BETEILIGTEDATEN_KEY) return -1
      if (b === BETEILIGTEDATEN_KEY) return 1
      if (a === LawFieldEnum.ALLGEMEIN) return -1
      if (b === LawFieldEnum.ALLGEMEIN) return 1
      return a.localeCompare(b)
    })
  })

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    if (expandedCategories.value.has(category)) {
      expandedCategories.value.delete(category)
    } else {
      expandedCategories.value.add(category)
    }
    // Force reactivity update
    expandedCategories.value = new Set(expandedCategories.value)
  }

  // Select a tag
  const selectTag = (tag: GenericDataMapper) => {
    emit('update:modelValue', tag.key)
    isMainPopoverOpen.value = false
  }

  // Clear selection
  const clearSelection = () => {
    emit('update:modelValue', null)
  }

  // Get selected tag
  const selectedTag = computed(() => {
    if (!props.modelValue) return null
    return props.tags.find((tag) => tag.key === props.modelValue) || null
  })

  // Expand all categories that have matching results when searching
  watch(fieldSearchQuery, (newQuery) => {
    if (newQuery.trim()) {
      // Expand all categories that have matching results
      sortedCategories.value.forEach((cat) => {
        expandedCategories.value.add(cat)
      })
      expandedCategories.value = new Set(expandedCategories.value)
    }
  })

  // Close filter popover when clicking outside
  const handleFilterPopoverOpenChange = (open: boolean) => {
    isFilterPopoverOpen.value = open
    if (!open) {
      filterSearchQuery.value = ''
    }
  }

  // Number of selected filters to show in badge (only count categories that actually exist)
  const selectedFilterCount = computed(() => {
    return allCategories.value.filter((cat) => selectedFilterCategories.value.has(cat)).length
  })

  // Check if no field types are selected
  const noFieldTypesSelected = computed(() => {
    return (
      selectedFilterCategories.value.size === 0 ||
      allCategories.value.filter((cat) => selectedFilterCategories.value.has(cat)).length === 0
    )
  })

  // Info banner visibility - show only in law-specific sections
  const showInfoBanner = computed(() => {
    return props.sectionType === 'law_specific'
  })

  // Dismiss info banner
  const isInfoBannerDismissed = ref(false)
  const dismissInfoBanner = () => {
    isInfoBannerDismissed.value = true
  }

  // Expand all categories by default when popover opens
  watch(isMainPopoverOpen, (isOpen) => {
    if (isOpen) {
      // Expand all visible categories
      sortedCategories.value.forEach((cat) => {
        expandedCategories.value.add(cat)
      })
      expandedCategories.value = new Set(expandedCategories.value)
    }
  })

  // Also expand categories when they become available
  watch(
    sortedCategories,
    (categories) => {
      if (isMainPopoverOpen.value) {
        categories.forEach((cat) => {
          expandedCategories.value.add(cat)
        })
        expandedCategories.value = new Set(expandedCategories.value)
      }
    },
    { immediate: true }
  )

  // Get translated category label
  const getCategoryLabel = (category: string) => {
    if (category === BETEILIGTEDATEN_KEY) {
      return t('beteiligtedaten')
    }

    // Translate law field categories
    const lawFieldTranslations =
      locale.value === 'de' ? lawFieldsDE : locale.value === 'fr' ? lawFieldsFR : lawFieldsEN
    return lawFieldTranslations[category] || category
  }
</script>

<template>
  <div class="w-full">
    <Popover v-model:open="isMainPopoverOpen">
      <PopoverTrigger as-child>
        <Button
          variant="secondary"
          role="combobox"
          :aria-expanded="isMainPopoverOpen"
          :disabled="disabled"
          class="w-full justify-between border-gray-200 bg-white focus-visible:ring-2 focus-visible:ring-purple-500"
          :class="{ 'cursor-not-allowed bg-[#f9f9f9]': disabled }"
        >
          <span
            class="truncate"
            :class="{
              'text-gray-800': selectedTag !== null,
              'text-gray-500': selectedTag === null
            }"
          >
            {{ selectedTag ? selectedTag.label : t('notSelected') }}
          </span>
          <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        class="w-[--radix-popover-trigger-width] min-w-[400px] p-0"
        align="start"
      >
        <div class="flex flex-col">
          <!-- Search and Filter Row -->
          <div class="flex items-center gap-2 border-b border-gray-200 p-3">
            <div class="relative flex-1">
              <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                v-model="fieldSearchQuery"
                type="text"
                :placeholder="t('searchFields')"
                class="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm placeholder-gray-500
                  focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </div>

            <!-- Filter Dropdown -->
            <Popover
              :open="isFilterPopoverOpen"
              @update:open="handleFilterPopoverOpenChange"
            >
              <PopoverTrigger as-child>
                <button
                  type="button"
                  class="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-200 bg-white
                    px-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50
                    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
                >
                  <span>{{ t('fieldType') }}</span>
                  <span
                    v-if="selectedFilterCount > 0"
                    class="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-purple-600 px-1.5 text-xs
                      font-medium text-white"
                  >
                    {{ selectedFilterCount }}
                  </span>
                  <ChevronDown class="h-4 w-4 text-gray-500" />
                </button>
              </PopoverTrigger>

              <PopoverContent
                class="w-64 p-0"
                align="end"
              >
                <div class="flex flex-col">
                  <!-- Filter Search -->
                  <div class="border-b border-gray-200 p-2">
                    <div class="relative">
                      <Search
                        class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        v-model="filterSearchQuery"
                        type="text"
                        :placeholder="t('searchCategories')"
                        class="h-8 w-full rounded-md border border-gray-200 bg-white pl-8 pr-3 text-sm placeholder-gray-500
                          focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  <!-- Filter Options -->
                  <div class="max-h-64 overflow-y-auto py-1">
                    <!-- Select All -->
                    <div
                      class="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50"
                      @click="toggleSelectAll"
                    >
                      <Checkbox
                        :checked="isSomeSelected ? 'indeterminate' : isAllSelected"
                        class="h-4 w-4"
                      >
                        <Minus
                          v-if="isSomeSelected"
                          class="h-4 w-4"
                        />
                        <Check
                          v-else
                          class="h-4 w-4"
                        />
                      </Checkbox>
                      <span class="text-sm font-medium text-gray-700">{{ t('selectAll') }}</span>
                    </div>

                    <!-- Category Options -->
                    <template
                      v-for="category in filteredFilterCategories"
                      :key="category"
                    >
                      <TooltipProvider
                        v-if="category === BETEILIGTEDATEN_KEY && isBeteiligtedatenDisabled"
                      >
                        <Tooltip>
                          <TooltipTrigger as-child>
                            <div
                              class="flex cursor-not-allowed items-center gap-2 px-3 py-2 opacity-50"
                            >
                              <Checkbox
                                :checked="false"
                                disabled
                                class="h-4 w-4"
                              />
                              <span class="text-sm text-gray-500">
                                {{ getCategoryLabel(category) }}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p class="max-w-xs">{{ t('participantDataOnly') }}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div
                        v-else
                        class="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50"
                        @click="toggleFilterCategory(category)"
                      >
                        <Checkbox
                          :checked="selectedFilterCategories.has(category)"
                          class="h-4 w-4"
                        />
                        <span class="flex-1 text-sm text-gray-700">
                          {{ getCategoryLabel(category) }}
                        </span>
                        <span class="text-sm text-gray-500">
                          ({{ getCategoryCount(category) }})
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <!-- Info Banner -->
          <div
            v-if="showInfoBanner && !isInfoBannerDismissed"
            class="flex items-start gap-2 border-b border-gray-200 bg-blue-50 px-3 py-2"
          >
            <Info class="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
            <p class="flex-1 text-sm text-blue-800">{{ t('participantDataInfo') }}</p>
            <button
              type="button"
              class="flex-shrink-0 text-blue-600 hover:text-blue-800"
              @click="dismissInfoBanner"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <!-- Tree Structure -->
          <div class="max-h-80 overflow-y-auto">
            <!-- No field types selected message -->
            <template v-if="noFieldTypesSelected">
              <div class="px-3 py-8 text-center text-sm text-gray-500">
                {{ t('noFieldTypeSelected') }}
              </div>
            </template>
            <!-- No results from search -->
            <template v-else-if="sortedCategories.length === 0">
              <div class="px-3 py-8 text-center text-sm text-gray-500">
                {{ t('noResults') }}
              </div>
            </template>
            <template v-else>
              <div
                v-for="category in sortedCategories"
                :key="category"
                class="border-b border-gray-100 last:border-b-0"
              >
                <!-- Category Header -->
                <div
                  class="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50"
                  @click="toggleCategory(category)"
                >
                  <span class="flex-1 text-sm font-medium text-gray-800">
                    {{ getCategoryLabel(category) }} ({{
                      filteredGroupedTags[category]?.length || 0
                    }})
                  </span>
                  <component
                    :is="expandedCategories.has(category) ? ChevronUp : ChevronDown"
                    class="h-4 w-4 text-gray-400"
                  />
                </div>

                <!-- Category Items with Tree Lines -->
                <div
                  v-if="expandedCategories.has(category)"
                  class="relative pb-1"
                >
                  <!-- Vertical line spanning all items -->
                  <div class="absolute bottom-1 left-[22px] top-0 z-10 w-px bg-gray-300" />

                  <div
                    v-for="tag in filteredGroupedTags[category]"
                    :key="tag.key"
                    class="group relative flex cursor-pointer items-center py-1.5 pr-3"
                    @click="selectTag(tag)"
                  >
                    <!-- Background that starts after the vertical line -->
                    <div
                      class="absolute inset-y-0 left-8 right-0 -z-0 transition-colors group-hover:bg-gray-50"
                      :class="{ '!bg-purple-50': modelValue === tag.key }"
                    />
                    <!-- Spacer for the tree line area -->
                    <div class="w-10 flex-shrink-0" />
                    <span
                      class="relative z-10 flex-1 text-sm"
                      :class="{
                        'font-medium text-purple-700': modelValue === tag.key,
                        'text-gray-700': modelValue !== tag.key
                      }"
                    >
                      {{ tag.label }}
                    </span>
                    <Check
                      v-if="modelValue === tag.key"
                      class="relative z-10 h-4 w-4 text-purple-600"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
